import { useState } from "react";
import { Column } from "../../../../../core/components/Column";
import { TextComponent } from "../../../../../core/components/TextComponent";
import { CustomButton } from "../../../../../core/components/CustomButton";
import { ImageUploader } from "../../../../../core/components/ImageUploader";
import { Container } from "../../../../../core/components/Container";
import { Camera } from "../../../../../core/icons/Camera";
import { useNavigate } from "react-router-dom";
import { setUserInfo } from "../../store/OnboardingSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../../core/store/store";
import { onCheckSignupPhotoLiveness } from "../../store/OnboardingThunk";

const SIGNUP_PHOTO_LIVENESS_SESSION_KEY = "signup_photo_liveness_pending";

const isSameFile = (left?: File | null, right?: File | null) => {
    if (!left || !right) {
        return false;
    }

    return (
        left.name === right.name &&
        left.size === right.size &&
        left.type === right.type &&
        left.lastModified === right.lastModified
    );
};

export function ProfilePicture() {
    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();
    const onboardingState = useSelector((state: RootState) => state.onboarding);
    const filesFromRedux = onboardingState.userInfo?.files || [];
    const [file, setFile] = useState<File | null>(filesFromRedux[0] || null);
    const [error, setError] = useState<string | null>(null);
    const [isChecking, setIsChecking] = useState(false);

    const handleOnClick = async () => {
        if (!file) {
            setError("Profile Picture is required");
            return;
        }
        setError(null);
        setIsChecking(true);
        const maritalStatusId = onboardingState.userInfo?.marital_status_id;
        const updatedFiles = filesFromRedux.length > 0
            ? [file, ...filesFromRedux.slice(1)]
            : [file];
        const existingApprovalToken = onboardingState.userInfo?.signup_photo_liveness_token;
        const approvedFirstFile = filesFromRedux[0] || null;

        if (existingApprovalToken && isSameFile(file, approvedFirstFile)) {
            window.sessionStorage.removeItem(SIGNUP_PHOTO_LIVENESS_SESSION_KEY);
            dispatch(
                setUserInfo({
                    files: updatedFiles,
                })
            );
            navigate("/addimage");
            return;
        }

        dispatch(
            setUserInfo({
                files: updatedFiles,
                signup_photo_liveness_token: undefined,
            })
        );
        window.sessionStorage.removeItem(SIGNUP_PHOTO_LIVENESS_SESSION_KEY);

        const action = await dispatch(
            onCheckSignupPhotoLiveness({
                file,
                maritalStatusId,
            })
        );
        setIsChecking(false);

        if (onCheckSignupPhotoLiveness.fulfilled.match(action)) {
            if (action.payload.shouldBlock) {
                setError(
                    action.payload.message ||
                        action.payload.reason ||
                        "This first profile photo cannot be used. Please try a different photo."
                );
                return;
            }

            if (action.payload.requiresLiveness) {
                if (!action.payload.livenessSession) {
                    setError(
                        action.payload.message ||
                            action.payload.reason ||
                            "Live check is required, but the session details are missing."
                    );
                    return;
                }
                window.sessionStorage.setItem(
                    SIGNUP_PHOTO_LIVENESS_SESSION_KEY,
                    JSON.stringify(action.payload)
                );
                navigate("/profilepicture/liveness", {
                    state: { result: action.payload },
                });
                return;
            }

            navigate("/addimage");
            return;
        }

        setError(
            action.error.message || "Could not check your first profile photo. Please try again."
        );
    };

    return (
        <Column
            crossAxisAlignment="center"
            className="md:bg-white px-10 pb-10 md:my-10 md:p-20 self-center text-center w-full md:w-[584px] lg:shadow-xl rounded-lg gap-10"
        >
            <Container className="relative">
                <ImageUploader
                    defaultImage="profilepicture.svg"
                    className="w-[198px] h-[198px]"
                    inputId="fileInput"
                    file={file}
                    setFile={setFile}
                    cropToSquareOnUpload
                />
                <label htmlFor="fileInput" className="absolute -bottom-2 -right-2 h-10 w-10 cursor-pointer" aria-label="Add Image">
                    <Camera />
                </label>
            </Container>
            <Container>
                <TextComponent variant="h2" className="font-bold">
                    Add Profile Picture
                </TextComponent>
                <TextComponent variant="h6" className="text-[#6B6B6B] mt-2 md:w-[390px]">
                    You need to upload at least one image as part of the registration process. Once you have completed the registration, you will be able to add more photos to your profile.
                    <br />
                    <br />
                    Your profile image must not contain any nudity and be only of yourself.
                </TextComponent>
                <Column className="w-full gap-5 mt-4">
                    {error && (
                        <TextComponent variant="h6" className="text-red-500 mt-2">
                            {error}
                        </TextComponent>
                    )}
                    <CustomButton onClick={handleOnClick} loading={isChecking}>
                        Continue
                    </CustomButton>
                </Column>
            </Container>
        </Column>
    );
}
